import { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import {
  Type, Image as ImageIcon, MousePointer2, Minus, Layers, ChevronUp, ChevronDown,
  Trash2, Save, ArrowLeft, Code, Eye, Monitor, Smartphone, Sparkles, Plus, GripVertical
} from "lucide-react";
import { toast } from "sonner";

const BLOCK_TYPES = [
  { type: "heading", label: "Heading", icon: Type },
  { type: "text", label: "Paragraph", icon: Type },
  { type: "image", label: "Image", icon: ImageIcon },
  { type: "button", label: "Button", icon: MousePointer2 },
  { type: "divider", label: "Divider", icon: Minus },
  { type: "spacer", label: "Spacer", icon: Minus },
  { type: "columns", label: "2 Columns", icon: Layers },
  { type: "social", label: "Social Links", icon: Layers },
  { type: "footer", label: "Footer", icon: Layers },
  { type: "code", label: "HTML Block", icon: Code },
];

const newBlock = (type) => {
  const id = "b" + Math.random().toString(36).slice(2, 9);
  switch (type) {
    case "heading": return { id, type, content: "Your big idea here", style: { fontSize: "32px", fontWeight: "800", fontFamily: "Inter, sans-serif", color: "#0A0F29", textAlign: "left", padding: "16px 24px" } };
    case "text": return { id, type, content: "Write your paragraph. Keep it human, clear and short.", style: { fontSize: "16px", fontFamily: "Inter, sans-serif", color: "#334155", lineHeight: "1.6", padding: "8px 24px" } };
    case "image": return { id, type, src: "https://images.unsplash.com/photo-1518770660439-4636190af475?w=1200&q=80", alt: "", style: { padding: "16px 24px" } };
    case "button": return { id, type, content: "Get started →", href: "#", style: { background: "#0044FF", color: "#fff", padding: "12px 24px", borderRadius: "6px", fontFamily: "Inter, sans-serif", fontWeight: "600", textAlign: "left", margin: "16px 24px" } };
    case "divider": return { id, type, style: { borderColor: "#E2E8F0", margin: "16px 24px" } };
    case "spacer": return { id, type, style: { height: "32px" } };
    case "columns": return { id, type, left: "Column one content goes here.", right: "Column two content goes here.", style: { padding: "16px 24px", fontFamily: "Inter, sans-serif", fontSize: "15px" } };
    case "social": return { id, type, links: ["twitter", "linkedin", "facebook", "instagram"], style: { padding: "16px 24px", textAlign: "center" } };
    case "footer": return { id, type, content: "© {{year}} {{company}}. <a href='#'>Unsubscribe</a>", style: { fontSize: "12px", color: "#94a3b8", padding: "24px", textAlign: "center", fontFamily: "Inter, sans-serif" } };
    case "code": return { id, type, content: "<p style='font-family:Inter,sans-serif'>Raw HTML here</p>" };
    default: return { id, type };
  }
};

const renderBlockHtml = (b) => {
  const s = b.style || {};
  const styleStr = Object.entries(s).map(([k, v]) => `${k.replace(/[A-Z]/g, m => "-" + m.toLowerCase())}:${v}`).join(";");
  switch (b.type) {
    case "heading": return `<h1 style="${styleStr};margin:0">${escapeHtml(b.content)}</h1>`;
    case "text": return `<p style="${styleStr};margin:0">${escapeHtml(b.content)}</p>`;
    case "image": return `<div style="${styleStr};text-align:center"><img src="${b.src}" alt="${escapeHtml(b.alt || "")}" style="max-width:100%;display:inline-block;border-radius:6px" /></div>`;
    case "button": return `<div style="margin:${(s.margin || "16px 24px")}"><a href="${b.href}" style="display:inline-block;background:${s.background};color:${s.color};padding:${s.padding};border-radius:${s.borderRadius};font-family:${s.fontFamily};font-weight:${s.fontWeight};text-decoration:none">${escapeHtml(b.content)}</a></div>`;
    case "divider": return `<hr style="border:0;border-top:1px solid ${s.borderColor};margin:${s.margin}" />`;
    case "spacer": return `<div style="height:${s.height}"></div>`;
    case "columns": return `<table style="width:100%;${styleStr}"><tr><td style="width:50%;vertical-align:top;padding-right:8px">${escapeHtml(b.left)}</td><td style="width:50%;vertical-align:top;padding-left:8px">${escapeHtml(b.right)}</td></tr></table>`;
    case "social": return `<div style="${styleStr}">${(b.links || []).map(l => `<a href="#" style="display:inline-block;margin:0 6px;color:#0044FF;text-decoration:none;font-family:Inter,sans-serif;font-size:14px">${l}</a>`).join(" · ")}</div>`;
    case "footer": return `<div style="${styleStr}">${b.content}</div>`;
    case "code": return b.content || "";
    default: return "";
  }
};

const escapeHtml = (str = "") => String(str).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const buildHtml = (blocks) => `<div style="max-width:600px;margin:0 auto;font-family:Inter,sans-serif;background:#ffffff">${blocks.map(renderBlockHtml).join("")}</div>`;

export default function TemplateBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const [meta, setMeta] = useState({ name: "Untitled Template", subject: "Your subject line", category: "general" });
  const [blocks, setBlocks] = useState([]);
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState("desktop");
  const [tab, setTab] = useState("design");
  const [saving, setSaving] = useState(false);
  const dragId = useRef(null);

  useEffect(() => {
    if (!id) return;
    api.get("/templates").then(({ data }) => {
      const t = data.find(x => x.id === id);
      if (t) {
        setMeta({ name: t.name, subject: t.subject, category: t.category });
        setBlocks(t.blocks && t.blocks.length ? t.blocks : [newBlock("heading"), newBlock("text"), newBlock("button")]);
      }
    });
  }, [id]);

  useEffect(() => {
    if (!id && blocks.length === 0) {
      setBlocks([newBlock("heading"), newBlock("text"), newBlock("button"), newBlock("footer")]);
    }
  }, [id, blocks.length]);

  const add = (type) => {
    const b = newBlock(type);
    setBlocks((s) => [...s, b]);
    setSelected(b.id);
  };

  const updateSel = (patch) => {
    setBlocks((s) => s.map(b => b.id === selected ? { ...b, ...patch, style: { ...(b.style || {}), ...(patch.style || {}) } } : b));
  };

  const removeBlock = (bid) => {
    setBlocks((s) => s.filter(b => b.id !== bid));
    if (selected === bid) setSelected(null);
  };

  const move = (bid, dir) => {
    setBlocks((s) => {
      const i = s.findIndex(b => b.id === bid);
      if (i < 0) return s;
      const j = i + dir;
      if (j < 0 || j >= s.length) return s;
      const copy = [...s];
      [copy[i], copy[j]] = [copy[j], copy[i]];
      return copy;
    });
  };

  const onDragStart = (bid) => { dragId.current = bid; };
  const onDrop = (overId) => {
    const from = dragId.current;
    if (!from || from === overId) return;
    setBlocks((s) => {
      const fi = s.findIndex(b => b.id === from);
      const ti = s.findIndex(b => b.id === overId);
      if (fi < 0 || ti < 0) return s;
      const copy = [...s];
      const [moved] = copy.splice(fi, 1);
      copy.splice(ti, 0, moved);
      return copy;
    });
    dragId.current = null;
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = buildHtml(blocks);
      const payload = { ...meta, body, blocks };
      if (id) await api.patch(`/templates/${id}`, payload);
      else await api.post("/templates", payload);
      toast.success("Template saved");
      nav("/templates");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const sel = blocks.find(b => b.id === selected);
  const previewWidth = view === "mobile" ? "375px" : "100%";

  return (
    <div className="-m-8 min-h-[calc(100vh-4rem)] flex flex-col bg-slate-100" data-testid="template-builder">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => nav("/templates")} data-testid="builder-back" className="gap-1"><ArrowLeft className="w-4 h-4" /> Back</Button>
        <Input data-testid="builder-name" value={meta.name} onChange={(e) => setMeta({ ...meta, name: e.target.value })} className="max-w-xs font-semibold" style={{ fontFamily: "Inter, sans-serif" }} />
        <div className="ml-auto flex items-center gap-2">
          <Button data-testid="builder-view-desktop" variant={view === "desktop" ? "default" : "outline"} size="icon" onClick={() => setView("desktop")} className={view === "desktop" ? "bg-[#0044FF]" : ""}><Monitor className="w-4 h-4" /></Button>
          <Button data-testid="builder-view-mobile" variant={view === "mobile" ? "default" : "outline"} size="icon" onClick={() => setView("mobile")} className={view === "mobile" ? "bg-[#0044FF]" : ""}><Smartphone className="w-4 h-4" /></Button>
          <Button data-testid="builder-save" onClick={save} disabled={saving} className="bg-[#0044FF] hover:bg-[#0033CC] gap-2"><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}</Button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Left palette */}
        <aside className="w-60 bg-white border-r border-slate-200 p-4 overflow-y-auto" data-testid="builder-palette">
          <div className="text-xs uppercase mono font-semibold text-slate-500 mb-3">Elements</div>
          <div className="grid grid-cols-2 gap-2">
            {BLOCK_TYPES.map(b => {
              const Icon = b.icon;
              return (
                <button key={b.type} data-testid={`palette-${b.type}`} onClick={() => add(b.type)} className="flex flex-col items-center gap-1 p-3 rounded-md border border-slate-200 hover:border-[#0044FF] hover:bg-blue-50 transition-all">
                  <Icon className="w-4 h-4 text-slate-600" />
                  <span className="text-[11px] font-medium text-center">{b.label}</span>
                </button>
              );
            })}
          </div>
          <div className="mt-4 text-xs text-slate-400 leading-relaxed">Click any element to add it. Drag or use arrows to reorder.</div>
        </aside>

        {/* Center canvas */}
        <div className="flex-1 overflow-y-auto p-6">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="design" data-testid="builder-tab-design"><Eye className="w-4 h-4 mr-1" /> Design</TabsTrigger>
              <TabsTrigger value="code" data-testid="builder-tab-code"><Code className="w-4 h-4 mr-1" /> Code</TabsTrigger>
            </TabsList>

            <TabsContent value="design">
              <div className="bg-white rounded-md shadow-sm border border-slate-200 mx-auto overflow-hidden" style={{ width: previewWidth, maxWidth: "640px" }}>
                <div className="bg-slate-50 px-4 py-2 border-b border-slate-200 text-xs text-slate-500 flex items-center gap-2 mono">
                  <Input value={meta.subject} onChange={(e) => setMeta({ ...meta, subject: e.target.value })} placeholder="Subject line" data-testid="builder-subject" className="h-7 text-xs border-0 bg-transparent px-0" style={{ fontFamily: "Inter, sans-serif" }} />
                </div>
                <div className="p-2">
                  {blocks.length === 0 && <div className="text-center py-20 text-slate-400 text-sm">Add your first block →</div>}
                  {blocks.map((b, i) => (
                    <div
                      key={b.id}
                      draggable
                      onDragStart={() => onDragStart(b.id)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => onDrop(b.id)}
                      onClick={() => setSelected(b.id)}
                      data-testid={`canvas-block-${i}`}
                      className={`relative group rounded ${selected === b.id ? "outline outline-2 outline-[#0044FF] outline-offset-2" : "hover:outline hover:outline-1 hover:outline-blue-300"}`}
                    >
                      <div dangerouslySetInnerHTML={{ __html: renderBlockHtml(b) }} />
                      <div className="absolute top-1 right-1 hidden group-hover:flex bg-white border border-slate-200 rounded shadow-sm">
                        <button title="Move up" onClick={(e) => { e.stopPropagation(); move(b.id, -1); }} className="p-1 hover:bg-slate-50"><ChevronUp className="w-3 h-3" /></button>
                        <button title="Move down" onClick={(e) => { e.stopPropagation(); move(b.id, 1); }} className="p-1 hover:bg-slate-50"><ChevronDown className="w-3 h-3" /></button>
                        <button title="Drag handle" className="p-1 hover:bg-slate-50 cursor-grab"><GripVertical className="w-3 h-3" /></button>
                        <button title="Delete" onClick={(e) => { e.stopPropagation(); removeBlock(b.id); }} className="p-1 hover:bg-red-50"><Trash2 className="w-3 h-3 text-red-500" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="code">
              <div className="max-w-3xl mx-auto">
                <Label>HTML output</Label>
                <Textarea readOnly value={buildHtml(blocks)} rows={24} className="mono text-xs" data-testid="builder-code" />
                <p className="text-xs text-slate-500 mt-2">Read-only — edit blocks visually to update. Copy this into any email service.</p>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Right inspector */}
        <aside className="w-80 bg-white border-l border-slate-200 p-4 overflow-y-auto" data-testid="builder-inspector">
          {!sel && <div className="text-center text-slate-400 text-sm py-10">Select a block to edit its properties</div>}
          {sel && (
            <div className="space-y-3">
              <div className="text-xs uppercase mono font-semibold text-slate-500 mb-2">Editing: {sel.type}</div>

              {("content" in sel) && sel.type !== "code" && (
                <div><Label>Content</Label><Textarea data-testid="prop-content" value={sel.content} onChange={(e) => updateSel({ content: e.target.value })} rows={4} style={{ fontFamily: "Inter, sans-serif" }} /></div>
              )}
              {sel.type === "code" && (
                <div><Label>Raw HTML</Label><Textarea data-testid="prop-html" value={sel.content} onChange={(e) => updateSel({ content: e.target.value })} rows={6} className="mono text-xs" /></div>
              )}
              {sel.type === "image" && (
                <>
                  <div><Label>Image URL</Label><Input data-testid="prop-src" value={sel.src} onChange={(e) => updateSel({ src: e.target.value })} /></div>
                  <div><Label>Alt text</Label><Input value={sel.alt || ""} onChange={(e) => updateSel({ alt: e.target.value })} /></div>
                </>
              )}
              {sel.type === "button" && (
                <div><Label>Link URL</Label><Input data-testid="prop-href" value={sel.href} onChange={(e) => updateSel({ href: e.target.value })} /></div>
              )}
              {sel.type === "columns" && (
                <>
                  <div><Label>Left column</Label><Textarea value={sel.left} onChange={(e) => updateSel({ left: e.target.value })} rows={3} /></div>
                  <div><Label>Right column</Label><Textarea value={sel.right} onChange={(e) => updateSel({ right: e.target.value })} rows={3} /></div>
                </>
              )}

              {/* Style controls */}
              {sel.type !== "spacer" && sel.type !== "divider" && sel.type !== "code" && (
                <>
                  <div className="pt-3 border-t border-slate-200">
                    <Label>Font family</Label>
                    <Select value={sel.style?.fontFamily || "Inter, sans-serif"} onValueChange={(v) => updateSel({ style: { fontFamily: v } })}>
                      <SelectTrigger data-testid="prop-font-family"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Inter, sans-serif">Inter</SelectItem>
                        <SelectItem value="'Helvetica Neue', sans-serif">Helvetica Neue</SelectItem>
                        <SelectItem value="'Satoshi', sans-serif">Satoshi</SelectItem>
                        <SelectItem value="Arial, sans-serif">Arial</SelectItem>
                        <SelectItem value="Georgia, serif">Georgia</SelectItem>
                        <SelectItem value="'Playfair Display', serif">Playfair Display</SelectItem>
                        <SelectItem value="'JetBrains Mono', monospace">JetBrains Mono</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {(sel.type === "heading" || sel.type === "text" || sel.type === "footer") && (
                    <div>
                      <Label>Font size</Label>
                      <Input data-testid="prop-font-size" value={sel.style?.fontSize || ""} onChange={(e) => updateSel({ style: { fontSize: e.target.value } })} placeholder="16px" />
                    </div>
                  )}
                  {(sel.type === "heading" || sel.type === "text" || sel.type === "footer" || sel.type === "button") && (
                    <div>
                      <Label>Color</Label>
                      <div className="flex gap-2">
                        <input type="color" value={sel.style?.color || "#0A0F29"} onChange={(e) => updateSel({ style: { color: e.target.value } })} className="w-10 h-9 rounded border border-slate-200" />
                        <Input value={sel.style?.color || ""} onChange={(e) => updateSel({ style: { color: e.target.value } })} />
                      </div>
                    </div>
                  )}
                  {sel.type === "button" && (
                    <div>
                      <Label>Background</Label>
                      <div className="flex gap-2">
                        <input type="color" value={sel.style?.background || "#0044FF"} onChange={(e) => updateSel({ style: { background: e.target.value } })} className="w-10 h-9 rounded border border-slate-200" />
                        <Input value={sel.style?.background || ""} onChange={(e) => updateSel({ style: { background: e.target.value } })} />
                      </div>
                    </div>
                  )}
                  {(sel.type === "heading" || sel.type === "text") && (
                    <div>
                      <Label>Text align</Label>
                      <Select value={sel.style?.textAlign || "left"} onValueChange={(v) => updateSel({ style: { textAlign: v } })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="left">Left</SelectItem>
                          <SelectItem value="center">Center</SelectItem>
                          <SelectItem value="right">Right</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <Label>Padding</Label>
                    <Input data-testid="prop-padding" value={sel.style?.padding || ""} onChange={(e) => updateSel({ style: { padding: e.target.value } })} placeholder="8px 24px" />
                  </div>
                </>
              )}
              {sel.type === "spacer" && (
                <div>
                  <Label>Height</Label>
                  <Input value={sel.style?.height || ""} onChange={(e) => updateSel({ style: { height: e.target.value } })} placeholder="32px" />
                </div>
              )}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
